import { FunctionComponent, useEffect, useRef, useState } from "react";
import styles from "../AddContacts/AddContacts.module.css";
import { connect, useDispatch } from "react-redux";
import { themeColors } from "../../Components/theme";
import { showSnackbarAction } from "../../Redux/actions";
import "../../theme-vars.css";
import { Circles } from "react-loader-spinner";
import { useTheme, useMediaQuery, TextField, Grid } from "@mui/material";
import StyledSubmitButton from "../../Components/Buttons/StyledSubmitButton";
import {
  getWhatsAppConfigById,
  updateWhatsAppConfig,
} from "../../Services/organizations";
import InputTextValue from "../../Components/TextInput/InputTextValue";
import { styled } from "@mui/material/styles";
import { FormControlLabel, Switch } from "@material-ui/core";
import { useHistory } from "react-router-dom";

type props = {
  theme: any;
  organizationId: any;
};

const primaryColor = "#292744";

// 🔹 Custom styled TextField
const CustomTextArea = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
    fontSize: "var(--font-size-medium)",
    fontFamily: "var(--font-family-main)",
    "& fieldset": {
      borderColor: "#bfc7d5",
    },
    "&:hover fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`,
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`,
    },
  },
  "& .MuiInputBase-input": {
    padding: "14px",
    fontSize: 14,
    color: "#222",
  },
  "& .MuiInputLabel-root": {
    fontWeight: 500,
    fontSize: 14,
    color: primaryColor,
  },
});

// 🔹 Default Lead JSON Templates
const defaultLeadIncomingJson = JSON.stringify(
  {
    "Customer Name": "customer_name",
    "Contact Number": "contact_no",
    "Alternate Number": "alternate_no",
    "Country Code": "country_code",
    "Lead Type": "lead_type",
    "Email": "email",
    "Lead Source": "lead_source",
    "Organization Name": "organization_name",
    "Message": "Hii"
  },
  null,
  2
);

const defaultLeadTransferJson = JSON.stringify(
  {
    "Customer Name": "customer_name",
    "Contact Number": "contact_no",
    "Alternate Number": "alternate_no",
    "Country Code": "country_code",
    "Lead Type": "lead_type",
    "Email": "email",
    "Lead Source": "lead_source",
    "Organization Name": "organization_name",
    "Message": "Hii"
  },
  null,
  2
);

const SimplyWhatsAppIntegration: FunctionComponent<props> = ({ theme, organizationId }) => {
  const dispatcher = useDispatch();
  const history = useHistory();
  const [load, setLoad] = useState(false);
  const [list, setList] = useState<any[]>([]);
  const [instanceId, setInstanceId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [url, setUrl] = useState("");
  const [incomingMessage, setIncomingMessage] = useState("");
  const [transferMessage, setTransferMessage] = useState("");
  const textAreaIncomingRef = useRef<any>(null);
  const textAreaTransferRef = useRef<any>(null);
  const [simplyId, setSimplyId] = useState("");
  const [simplyActive, setSimplyActive] = useState(false);
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  // ✅ load data from Firestore
  useEffect(() => {
    if (!organizationId || organizationId.trim() === "") {
      console.warn("organizationId is empty → skipping Firestore call");
      return;
    }
    const unsub = getWhatsAppConfigById(organizationId, setList);
    return () => unsub();
  }, [organizationId]);

  // -------------------- Populate Fields --------------------
  useEffect(() => {
    if (!organizationId || !list || list.length === 0) return;
    const c = list[0];

    // If SIMPLY block missing → create fresh object
    if (!c.simply) {
      const newId = crypto.randomUUID();
      updateWhatsAppConfig(organizationId, {
        simply: {
          id: newId,
          type: "Simply WhatsApp",
        },
      });
      setSimplyId(newId);
      return;
    }

    // If id missing → create id
    if (!c.simply.id) {
      const newId = crypto.randomUUID();
      updateWhatsAppConfig(organizationId, {
        simply: { ...c.simply, id: newId, type: "Simply WhatsApp" },
      });
      setSimplyId(newId);
    } else {
      setSimplyId(c.simply.id);
    }

    // Set fields
    setInstanceId(c.simply.instance_id || "");
    setAccessToken(c.simply.access_token || "");
    setUrl(c.simply.url || "");
    setIncomingMessage(c.simply.incoming_json || "");
    setTransferMessage(c.simply.transfer_json || "");
    setSimplyActive(c.simply.active || false);

  }, [list]);

  // -------------------- Update Firestore --------------------
  const create = async (e: any) => {
    e.preventDefault();

    if (!instanceId || !accessToken || !url || !incomingMessage || !transferMessage) {
      return dispatcher(showSnackbarAction("Please fill all fields", "error"));
    }

    setLoad(true);
    setLoad(true);
    const payload = {
      simply: {
        id: simplyId,
        type: "Simply WhatsApp",        // <<------------------- IMPORTANT
        instance_id: instanceId,
        access_token: accessToken,
        url,
        incoming_json: incomingMessage,
        transfer_json: transferMessage,
        active: simplyActive,
      },
    };
    try {
      await updateWhatsAppConfig(organizationId, payload);
      dispatcher(showSnackbarAction("Updated successfully!", "success"));
      history.push("/whatsAppIntegration");
    } catch (err) {
      dispatcher(showSnackbarAction("Error updating config", "error"));
      console.log(err);
    }
    setLoad(false);
  };

  // ✅ Copy Handlers
  const handleCopyIncoming = () => {
    if (textAreaIncomingRef.current) {
      const textToCopy = textAreaIncomingRef.current.value;
      navigator.clipboard.writeText(textToCopy);
      dispatcher(showSnackbarAction("Incoming JSON copied!", "success"));
    }
  };

  const handleCopyTransfer = () => {
    if (textAreaTransferRef.current) {
      const textToCopy = textAreaTransferRef.current.value;
      navigator.clipboard.writeText(textToCopy);
      dispatcher(showSnackbarAction("Transfer JSON copied!", "success"));
    }
  };

  return (
    <div
      className={styles.parent}
      style={{
        backgroundColor: theme
          ? themeColors.backgroundColor
          : themeColors.backgroundColorDark,
        color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
        marginTop: isMobile ? "6%" : "4%",
        fontFamily: "var(--font-family-main)",
      }}
    >
      <div
        className="w-100 d-flex flex-column gap-2 px-4"
        style={{ maxWidth: isMobile ? "100vw" : undefined, margin: "0 auto" }}
      >
        <form
          className="card p-4 shadow rounded mt-2"
          style={{
            backgroundColor: theme
              ? themeColors.cardBackgroundColor
              : themeColors.cardBackgroundColorDark,
          }}
        >
          <Grid container spacing={2}>
            {/* Config fields */}
            <Grid item xs={12} sm={4}>
              <InputTextValue
                title="Instance ID"
                required
                value={instanceId}
                onChange={(e: any) => setInstanceId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <InputTextValue
                title="Access Token"
                required
                value={accessToken}
                onChange={(e: any) => setAccessToken(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <InputTextValue
                title="URL"
                required
                value={url}
                onChange={(e: any) => setUrl(e.target.value)}
              />
            </Grid>
            {/* 🟢 Lead Incoming Section */}
            <Grid item xs={12} sm={6}>
              <div style={{ position: "relative" }}>
                <CustomTextArea
                  inputRef={textAreaIncomingRef}
                  fullWidth
                  label="Lead (Incoming)"
                  variant="outlined"
                  multiline
                  rows={10}
                  onChange={(e: any) => setIncomingMessage(e.target.value)}
                  value={incomingMessage?.trim() ? incomingMessage : defaultLeadIncomingJson}
                />
                <button
                  type="button"
                  onClick={handleCopyIncoming}
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 12,
                    backgroundColor: primaryColor,
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "4px 10px",
                    cursor: "pointer",
                    fontFamily: "var(--font-family-main)",
                    fontSize: 13,
                  }}
                >
                  Copy
                </button>
              </div>
            </Grid>
            {/* 🟣 Lead Transfer Section */}
            <Grid item xs={12} sm={6}>
              <div style={{ position: "relative" }}>
                <CustomTextArea
                  inputRef={textAreaTransferRef}
                  fullWidth
                  label="Lead (Transfer)"
                  variant="outlined"
                  multiline
                  rows={10}
                  onChange={(e: any) => setTransferMessage(e.target.value)}
                  value={transferMessage?.trim() ? transferMessage : defaultLeadTransferJson}
                />
                <button
                  type="button"
                  onClick={handleCopyTransfer}
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 12,
                    backgroundColor: primaryColor,
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "4px 10px",
                    cursor: "pointer",
                    fontFamily: "var(--font-family-main)",
                    fontSize: 13,
                  }}
                >
                  Copy
                </button>
              </div>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12} style={{ marginTop: 12 }}>
              <StyledSubmitButton
                onClick={create}
                themeMode={themeMUI.palette.mode}
                isMobile={isMobile}
              >
                {load ? (
                  <span style={{ display: "flex", justifyContent: "center" }}>
                    <Circles color="#fff" height={20} width={20} />
                    &nbsp;Submit...
                  </span>
                ) : (
                  <>Submit</>
                )}
              </StyledSubmitButton>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  theme: state.theme.isLightMode,
  organizationId: state.organization.id,
});

export default connect(mapStateToProps)(SimplyWhatsAppIntegration);

